import { ref, computed, inject } from 'vue'
import { useStore } from 'vuex'
import { SubmissionFileType, SubmissionFile, ShowToastType } from '@/types'
import { validateDuplicateFiles, validateFiles } from '../services/validation/uploadFiles'
import validateUploadFilesView from '../services/validation/uploadFiles'
import { trackEvent } from '../services/gtm'
import { AxiosError } from 'axios'
import { MAX_FILENAME_LENGTH } from '@/constants/app'

/**
 * Composable for managing file upload functionality.
 * Handles file uploads, deletions, validation, error handling, and user feedback.
 * Uses hybrid approach: Vuex mutations for file state (shared across multiple upload components),
 * but API calls, error handling, and validation orchestration in composable.
 * @returns Object with reactive properties and methods for file upload management
 */
export function useFileUpload() {
  const store = useStore()
  const showToast = inject('showToast') as ((options: ShowToastType) => void) | undefined

  // State
  const isUploading = ref(false)
  const uploadError = ref<string | undefined>(undefined)
  const localErrors = ref<Array<{ fileName: string, text: string }>>([])

  // Computed properties
  const submissionId = computed(() => store.state.activeSubmission.submissionId)

  const submissionFiles = computed(() => store.state.activeSubmission.files as SubmissionFile[])

  /**
   * Gets files by type
   * @param fileType - Type of submission file
   */
  const getFilesByType = (fileType: SubmissionFileType): SubmissionFile[] => {
    return submissionFiles.value.filter((file: SubmissionFile) => file.type === fileType)
  }

  /**
   * Checks if a file type has been uploaded
   * @param fileType - Type of submission file
   */
  const hasFileOfType = (fileType: SubmissionFileType): boolean => {
    return submissionFiles.value.some((file: SubmissionFile) => file.type === fileType)
  }

  /**
   * Gets a specific file
   * @param fileId - ID of the file
   */
  const getFileById = (fileId: string): SubmissionFile | undefined => {
    return submissionFiles.value.find((file: SubmissionFile) => file.id === fileId)
  }

  /**
   * Utility: Checks if error is an Axios error
   */
  const isAxiosError = (error: unknown): error is AxiosError => {
    return typeof error === 'object' && error !== null && 'isAxiosError' in error
  }

  /**
   * Utility: Shows success toast notification
   * @param message - Success message
   * @param location - File type location
   * @param fileType - File extension type
   */
  const showSuccessToast = (message: string, location: SubmissionFileType, fileType: string) => {
    if (!showToast) return
    showToast({
      variant: 'success',
      headingText: 'Success',
      bodyContent: message,
      location,
      isManual: false,
      isUpload: true,
      fileType,
    })
  }

  /**
   * Helper to track upload error events
   */
  const trackUploadErrorEvent = (
    error: AxiosError | unknown,
    fileType: string,
    location: SubmissionFileType,
    fileId?: string,
  ) => {
    const status = isAxiosError(error) ? error.response?.status : undefined
    const message = isAxiosError(error) ? error.message : 'Unknown error'
    trackEvent({
      category: 'Server Errors',
      action: 'error',
      event: `Upload${location}FileError`,
      fileType,
      location,
      fileId: fileId || 'unknown',
      errorType: status ? `HTTP${status}` : 'UnknownError',
      errorDetails: message || 'No error message available',
      submissionId: submissionId.value,
    })
  }

  /**
   * Helper to track validation error events
   */
  const trackValidationErrorEvent = (
    errorType: string,
    errorDetails: string,
    location: SubmissionFileType,
  ) => {
    trackEvent({
      category: 'Error',
      action: 'select file',
      event: `Upload${location}FileError`,
      location,
      errorType,
      errorDetails,
      submissionId: submissionId.value,
    })
  }

  /**
   * Helper to track delete error events
   */
  const trackDeleteErrorEvent = (
    error: AxiosError | unknown,
    fileId: string,
    location: SubmissionFileType,
  ) => {
    const status = isAxiosError(error) ? error.response?.status : undefined
    const message = isAxiosError(error) ? error.message : 'Unknown error'
    trackEvent({
      category: 'Server Errors',
      action: 'error',
      event: `Delete${location}FileError`,
      location,
      fileId,
      errorType: status ? `HTTP${status}` : 'UnknownError',
      errorDetails: message || 'No error message available',
      submissionId: submissionId.value,
    })
  }

  /**
   * Helper to track form input validation errors
   */
  const trackFormInputErrorEvent = (
    fieldName: string,
    errorMessage: string,
    additionalData?: Record<string, unknown>,
  ) => {
    trackEvent({
      event: 'FormInputError',
      category: 'Form Validation',
      action: 'validation error',
      label: fieldName,
      formField: fieldName,
      errorMessage,
      ...additionalData,
    })
  }

  /**
   * Validates a file before upload
   * @param file - File to validate
   * @param fileType - Type of submission file
   * @param validationKey - Key for file validation (e.g., 'mainarticle', 'coverletter')
   * @returns Validation result with valid status and potential errors
   */
  const validateFileForUpload = async (
    file: File,
    fileType: SubmissionFileType,
    validationKey: string,
  ): Promise<{ valid: boolean, errorType?: string, errorMessages?: string }> => {
    // Check filename length
    if (file.name.length > MAX_FILENAME_LENGTH) {
      const errorMsg = `${file.name}: File name must be ${MAX_FILENAME_LENGTH} characters or fewer. Current length: ${file.name.length}.`
      trackValidationErrorEvent('FilenameTooLong', 'Character limit exceeded', fileType)
      // Strip filename from error message for tracking (sensitive data)
      const sanitisedErrorMsg = `File name must be ${MAX_FILENAME_LENGTH} characters or fewer. Current length: ${file.name.length}.`
      trackFormInputErrorEvent(
        fileType,
        sanitisedErrorMsg,
        {
          validationKey,
          submissionId: submissionId.value,
        },
      )
      return {
        valid: false,
        errorType: 'Filename Too Long',
        errorMessages: errorMsg,
      }
    }

    // Check for duplicates and validate file type/size
    const duplicateValidation = validateDuplicateFiles([file])
    const fileValidation = validateFiles([file], validationKey)
    const [resultDuplicate, resultFiles] = await Promise.all([duplicateValidation, fileValidation])

    if (!resultFiles.valid || !resultDuplicate.valid) {
      const errorType = !resultFiles.valid ? resultFiles.errorType : resultDuplicate.errorType
      const errorMessages = !resultFiles.valid ? resultFiles.errors : resultDuplicate.errors
      const errorMsg = errorMessages.toString()

      // Strip filename from error message for tracking (sensitive data)
      // Pattern: "filename.ext: error message" -> "error message"
      const sanitisedErrorMsg = errorMsg.replace(/^[^:]+\.[a-zA-Z0-9]+:\s*/, '')
      trackFormInputErrorEvent(
        fileType,
        sanitisedErrorMsg,
        {
          validationKey,
          errorType,
          submissionId: submissionId.value,
        },
      )
      return {
        valid: false,
        errorType,
        errorMessages: errorMsg,
      }
    }

    return { valid: true }
  }

  /**
   * Uploads a single file
   * @param file - File to upload
   * @param fileType - Type of submission file
   * @param validationKey - Key for file validation (e.g., 'mainarticle', 'coverletter')
   * @param options - Optional configuration
   * @returns Promise that resolves when upload is complete
   */
  const uploadFile = async (
    file: File,
    fileType: SubmissionFileType,
    validationKey: string,
    options: {
      onSuccess?: (file: File, fileExtension: string) => void
      onError?: (error: unknown, file: File) => void
      onValidationError?: (error: unknown, file: File) => void
      successMessage?: string
    } = {},
  ): Promise<void> => {
    isUploading.value = true
    uploadError.value = undefined
    const fileExtension = file.name.split('.').pop() || 'unknown'

    try {
      // Validate file
      const validation = await validateFileForUpload(file, fileType, validationKey)
      if (!validation.valid) {
        // Don't show toast for validation errors - components handle inline error display
        // Call validation error callback to allow component to display inline error
        if (options.onValidationError) {
          options.onValidationError(
            new Error(validation.errorMessages || 'File validation failed'),
            file,
          )
        }
        // Don't call onError for validation failures - only for upload API failures
        return
      }

      // Upload file via store action
      await store.dispatch('uploadFile', { fileType, file })

      // Show success toast
      const successMessage = options.successMessage || `Your ${validationKey} has been successfully added.`
      showSuccessToast(successMessage, fileType, fileExtension)

      // Call success callback
      if (options.onSuccess) {
        options.onSuccess(file, fileExtension)
      }
    }
    catch (error) {
      uploadError.value = 'Failed to upload file'

      // Track error
      const uploadedFile = submissionFiles.value.find(
        (f: SubmissionFile) => f.type === fileType && f.file === file.name,
      )
      trackUploadErrorEvent(error, fileExtension, fileType, uploadedFile?.id)

      // Remove file from store on error
      store.commit('removeSubmissionFile', { fileType, fileName: file.name })

      // Call error callback (only for API upload failures, not validation)
      if (options.onError) {
        options.onError(error, file)
      }
      else {
        // Only throw if no error callback provided
        throw error
      }
    }
    finally {
      isUploading.value = false
    }
  }

  /**
   * Uploads multiple files
   * @param files - Array of files to upload
   * @param fileType - Type of submission file
   * @param validationKey - Key for file validation
   * @param options - Optional configuration
   */
  const uploadMultipleFiles = async (
    files: File[] | FileList,
    fileType: SubmissionFileType,
    validationKey: string,
    options: {
      onFileSuccess?: (file: File, fileExtension: string) => void
      onFileError?: (error: unknown, file: File) => void
      onValidationError?: (error: unknown, file: File) => void
      onAllComplete?: () => void
      successMessage?: (fileName: string) => string
    } = {},
  ): Promise<void> => {
    for (const file of Array.from(files)) {
      try {
        await uploadFile(file, fileType, validationKey, {
          onSuccess: (uploadedFile, fileExtension) => {
            localErrors.value = localErrors.value.filter(e => e.fileName !== uploadedFile.name)
            if (options.onFileSuccess) {
              options.onFileSuccess(uploadedFile, fileExtension)
            }
          },
          onError: (error, failedFile) => {
            if (options.onFileError) {
              options.onFileError(error, failedFile)
            }
          },
          onValidationError: (error, failedFile) => {
            // Let component handle validation error display via callback
            if (options.onValidationError) {
              options.onValidationError(error, failedFile)
            }
          },
          successMessage: options.successMessage ? options.successMessage(file.name) : undefined,
        })
      }
      catch {
        // Error already handled in uploadFile
        continue
      }
    }

    if (options.onAllComplete) {
      options.onAllComplete()
    }
  }

  /**
   * Deletes a file
   * @param fileId - ID of the file to delete
   * @param options - Optional configuration
   * @returns Promise that resolves when deletion is complete
   */
  const deleteFile = async (
    fileId: string,
    options: {
      onSuccess?: (fileId: string) => void
      onError?: (error: unknown, fileId: string) => void
    } = {},
  ): Promise<void> => {
    try {
      const file = getFileById(fileId)
      if (!file) {
        throw new Error(`File with ID ${fileId} not found`)
      }

      await store.dispatch('deleteFile', fileId)

      if (options.onSuccess) {
        options.onSuccess(fileId)
      }
    }
    catch (error) {
      const file = getFileById(fileId)
      if (file) {
        trackDeleteErrorEvent(error, fileId, file.type)
      }

      if (options.onError) {
        options.onError(error, fileId)
      }
      else {
        // Only throw if no error callback provided
        throw error
      }
    }
  }

  /**
   * Removes a file from local state (mutation only, no API call)
   * Used when upload fails or when we need to clean up optimistic updates
   * @param fileType - Type of submission file
   * @param fileName - File name to remove
   */
  const removeSubmissionFile = (fileType: SubmissionFileType, fileName: string) => {
    store.commit('removeSubmissionFile', { fileType, fileName })
  }

  /**
   * Validates all files in the upload view
   */
  const validateUploadFiles = async () => {
    try {
      await validateUploadFilesView(store)
    }
    catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown validation error'
      uploadError.value = errorMsg
      trackEvent({
        category: 'Error',
        action: 'validation',
        event: 'validateUploadFilesError',
        location: 'UploadFiles',
        errorType: 'ValidationError',
        errorDetails: errorMsg,
        submissionId: submissionId.value,
      })
    }
  }

  /**
   * Clears all local errors
   */
  const clearErrors = () => {
    uploadError.value = undefined
    localErrors.value = []
  }

  /**
   * Clears error for a specific file
   * @param fileName - Name of the file
   */
  const clearFileError = (fileName: string) => {
    localErrors.value = localErrors.value.filter(e => e.fileName !== fileName)
  }

  return {
    // State
    isUploading,
    uploadError,
    localErrors,

    // Computed
    submissionId,
    submissionFiles,

    // File Query Methods
    getFilesByType,
    hasFileOfType,
    getFileById,

    // Upload Methods
    uploadFile,
    uploadMultipleFiles,

    // Delete Methods
    deleteFile,
    removeSubmissionFile,

    // Validation Methods
    validateFileForUpload,
    validateUploadFiles,

    // Error Management
    clearErrors,
    clearFileError,
  }
}
